import { loanEpochsAtomsContainer } from "@/financeModels/owasco/epoch";
import { isConventionalLoanTypeAtom } from "@/financeModels/owasco/general";
import {
  addbacksAndAdjustmentsItemization,
  amortOfDealExpensesMolecule,
  annualDeprAmortizationExpensesMolecule,
  annualMaintenanceCapExMolecule,
  buyersAdjustedEBITDAMarginsMolecule,
  buyersAdjustedEBITDAMolecule,
  buyersCompensationMolecule,
  buyersPurchaseMultipleMolecule,
  companysTaxRateMolecule,
  costOfGoodsSoldItemization,
  distributableFreeCashFlowMolecule,
  dscrMolecule,
  grossMarginsMolecule,
  grossProfitMolecule,
  grossProfitPercentGrowthMolecule,
  grossRevenueItemization,
  grossRevenuePercentGrowthMolecule,
  incomeTaxesMolecule,
  netIncomeGrowthMolecule,
  netIncomeMolecule,
  newOperatingExpensesItemization,
  operatingExpensesItemization,
  otherIncomeMolecule,
  sba7aLoanExpenseInterestMolecule,
  sba7aLoanExpensePricipalMolecule,
  sba7aLoanExpenseTotalMolecule,
  sellersDeprAmortMolecule,
  sellersDiscretionaryEarningsGrowthMolecule,
  sellersDiscretionaryEarningsMarginsMolecule,
  sellersDiscretionaryEarningsMolecule,
  sellersEBITDAMolecule,
  sellersIncomeTaxMolecule,
  sellersInterestExpenseMolecule,
  sellersNoteExpenseInterestMolecule,
  sellersNoteExpensePricipalMolecule,
  sellersNoteExpenseTotalMolecule,
  taxableIncomeMolecule,
  totalDebtServiceMolecule,
  totalEquityInvestorDistributionsMolecule,
  totalEquityPreferredDividendsMolecule,
  totalEquityPrincipalRepaymentsMolecule,
  totalEquityProfitDistributionsMolecule,
  unleveredFreeCashFlowMolecule,
  useableFreeCashFlowMolecule,
} from "@/financeModels/owasco/lookback";
import { Getter, atom } from "jotai";
import {
  boldStyle,
  createEmptyRow,
  createGroupEndRow,
  createGroupStartRow,
  createTitleRow,
  doubleTopBorderStyle,
  greyHighlightStyle,
  groupWorksheetRows,
  italicStyle,
  itemizationToRows,
  moleculeToRow,
  singleTopBorderStyle,
  toSpreadsheetCell,
} from "./helper";

const XLSXStub = () => import("xlsx-js-style");
type XLSXModule = Awaited<ReturnType<typeof XLSXStub>>;
const { orderedEpochsMolecule } = loanEpochsAtomsContainer;

const formatEpochLabel = (epoch: {
  type: "full" | "partial" | "trailingTwelveMonths";
  date: number;
  duration?: { startDate: number; endDate: number };
}) => {
  const startDate =
    epoch.type === "full"
      ? new Date(epoch.date)
      : new Date(epoch.duration?.startDate || epoch.date);
  const endDate =
    epoch.type === "full"
      ? new Date(epoch.date)
      : new Date(epoch.duration?.endDate || epoch.date);

  const startMonth = startDate.toLocaleString("en-US", { month: "short" });
  const endMonth = endDate.toLocaleString("en-US", { month: "short" });

  return `${startMonth} ${startDate.getFullYear()} - ${endMonth} ${endDate.getFullYear()}`;
};

const formatEpochMonths = (epoch: {
  type: "full" | "partial" | "trailingTwelveMonths";
  annualized?: boolean;
  duration?: { startDate: number; endDate: number };
}) => {
  if (epoch.type === "full" && epoch.annualized) return "PY Annualized";

  const months =
    epoch.type === "full"
      ? 12
      : epoch.duration
        ? (new Date(epoch.duration.endDate).getFullYear() -
            new Date(epoch.duration.startDate).getFullYear()) *
            12 +
          (new Date(epoch.duration.endDate).getMonth() -
            new Date(epoch.duration.startDate).getMonth()) +
          1
        : 0;

  return `(${months} month${months !== 1 ? "s" : ""})`;
};

export const buildHistoricalsWorksheet = (get: Getter, XLSX: XLSXModule) => {
  const isConventionalLoanType = get(isConventionalLoanTypeAtom);

  const pageTitle = (window.document.title || "ETA IQ Historicals")
    .replace("Model (", "")
    .replace(")", "");

  const epochs = get(orderedEpochsMolecule).map((epochAtom) => get(epochAtom));

  const rows = [
    [pageTitle],
    ["Created on - " + new Date().toLocaleString()],
    createEmptyRow(),
    ["DSCR", ...get(dscrMolecule)].map((cell) =>
      toSpreadsheetCell(get, cell, { type: "dscr" }),
    ),

    createEmptyRow(),
    createTitleRow("COMPANY FINANCIALS"),
    createEmptyRow(),
    [undefined, ...epochs.map((epoch) => formatEpochLabel(epoch))],
    [undefined, ...epochs.map((epoch) => formatEpochMonths(epoch))],
    createEmptyRow(),

    ...itemizationToRows(get, "Gross Revenue", grossRevenueItemization),
    moleculeToRow(get, grossRevenuePercentGrowthMolecule, {
      label: "Revenue Growth (%)",
      type: "percent",
      style: italicStyle,
    }),
    ...itemizationToRows(
      get,
      "Cost of Goods Sold (COGS)",
      costOfGoodsSoldItemization,
      { isNegative: true },
    ),
    moleculeToRow(get, grossProfitMolecule, {
      label: "Gross Profit",
      style: doubleTopBorderStyle,
    }),
    createEmptyRow(),
    moleculeToRow(get, grossProfitPercentGrowthMolecule, {
      label: "Gross Profit Growth (%)",
      type: "percent",
      style: italicStyle,
    }),
    moleculeToRow(get, grossMarginsMolecule, {
      label: "Gross Margins (%)",
      type: "percent",
      style: italicStyle,
    }),
    createEmptyRow(),
    ...itemizationToRows(
      get,
      "Operating Expenses",
      operatingExpensesItemization,
      {
        isNegative: true,
      },
    ),
    moleculeToRow(get, otherIncomeMolecule, {
      label: "Other Income",
    }),
    moleculeToRow(get, netIncomeMolecule, {
      label: "Net Income",
      style: doubleTopBorderStyle,
    }),
    moleculeToRow(get, netIncomeGrowthMolecule, {
      label: "Net Income Growth (%)",
      type: "percent",
      style: italicStyle,
    }),

    createEmptyRow(),
    createTitleRow("SELLER'S FINANCIAL ADJUSTMENTS"),
    createEmptyRow(),

    moleculeToRow(get, sellersInterestExpenseMolecule, {
      label: "Seller's Interest Expenses",
    }),
    moleculeToRow(get, sellersDeprAmortMolecule, {
      label: "Seller's Depreciation & Amortization",
    }),
    moleculeToRow(get, sellersIncomeTaxMolecule, {
      label: "Seller's Income Taxes",
    }),
    moleculeToRow(get, sellersEBITDAMolecule, {
      label: "Seller's EBITDA",
      style: doubleTopBorderStyle,
    }),
    ...itemizationToRows(
      get,
      "Seller's Addbacks",
      addbacksAndAdjustmentsItemization,
    ),
    moleculeToRow(get, sellersDiscretionaryEarningsMolecule, {
      label: "Seller's Adjusted EBITDA (SDE)",
      style: doubleTopBorderStyle,
    }),

    createEmptyRow(),
    moleculeToRow(get, sellersDiscretionaryEarningsGrowthMolecule, {
      label: "Seller's Adj. EBITDA Growth (%)",
      type: "percent",
      style: italicStyle,
    }),
    moleculeToRow(get, sellersDiscretionaryEarningsMarginsMolecule, {
      label: "Seller's Adj. EBITDA Margins (%)",
      type: "percent",
      style: italicStyle,
    }),
    createEmptyRow(),

    createEmptyRow(),
    createTitleRow("BUYER'S PRO-FORMA ADJUSTMENTS"),
    createEmptyRow(),

    moleculeToRow(get, buyersCompensationMolecule, {
      label: "Buyer's Compensation",
      isNegative: true,
    }),
    ...itemizationToRows(
      get,
      "New Operating Expenses",
      newOperatingExpensesItemization,
      { isNegative: true },
    ),
    moleculeToRow(get, buyersAdjustedEBITDAMolecule, {
      label: "Buyer's Adjusted EBITDA",
      style: doubleTopBorderStyle,
    }),

    createEmptyRow(),
    moleculeToRow(get, buyersAdjustedEBITDAMarginsMolecule, {
      label: "Buyer's Adjusted EBITDA Margins (%)",
      type: "percent",
      style: italicStyle,
    }),
    moleculeToRow(get, buyersPurchaseMultipleMolecule, {
      label: "Buyer's Purchase Multiple",
      type: "dscr",
      style: italicStyle,
    }),
    createEmptyRow(),

    createEmptyRow(),
    createTitleRow("CASH FLOW METRICS & DISTRIBUTIONS"),
    createEmptyRow(),

    createGroupStartRow(),
    moleculeToRow(get, buyersAdjustedEBITDAMolecule, {
      label: "Buyer's Adjusted EBITDA",
      style: boldStyle,
    }),
    createGroupStartRow(),
    moleculeToRow(get, incomeTaxesMolecule, {
      label: "Income Taxes",
      isNegative: true,
      style: singleTopBorderStyle,
    }),
    moleculeToRow(get, sba7aLoanExpenseInterestMolecule, {
      label: isConventionalLoanType
        ? "Interest - Senior Debt"
        : "Interest - SBA 7(a) Loan",
      isNegative: true,
    }),
    moleculeToRow(get, sellersNoteExpenseInterestMolecule, {
      label: "Interest - Seller's Note",
      isNegative: true,
    }),
    moleculeToRow(get, annualDeprAmortizationExpensesMolecule, {
      label: "Depreciation & Amortization",
      isNegative: true,
    }),
    moleculeToRow(get, amortOfDealExpensesMolecule, {
      label: "Amortization of Deal Expenses",
      isNegative: true,
    }),
    moleculeToRow(get, taxableIncomeMolecule, {
      label: "Taxable Income",
      style: doubleTopBorderStyle,
    }),
    moleculeToRow(get, companysTaxRateMolecule, {
      label: "Company's Tax Rate",
      type: "percent",
      style: italicStyle,
    }),
    createGroupEndRow(),
    moleculeToRow(get, annualMaintenanceCapExMolecule, {
      label: "Annual Maintenance CapEx",
      isNegative: true,
    }),
    createGroupEndRow(),

    moleculeToRow(get, unleveredFreeCashFlowMolecule, {
      label: "Unlevered Free Cash Flow",
      style: greyHighlightStyle,
    }),

    createEmptyRow(),

    createGroupStartRow(),
    moleculeToRow(get, unleveredFreeCashFlowMolecule, {
      label: "Unlevered Free Cash Flow",
      style: boldStyle,
    }),
    createGroupStartRow(),
    moleculeToRow(get, sba7aLoanExpenseTotalMolecule, {
      label: isConventionalLoanType
        ? "Senior Debt Loan Total"
        : "SBA 7(a) Loan Total",
      isNegative: true,
      style: singleTopBorderStyle,
    }),
    moleculeToRow(get, sba7aLoanExpensePricipalMolecule, {
      label: isConventionalLoanType
        ? "Senior Debt - Principal"
        : "Principal - SBA 7(a) Loan",
      isNegative: true,
    }),
    moleculeToRow(get, sba7aLoanExpenseInterestMolecule, {
      label: isConventionalLoanType
        ? "Interest - Senior Debt"
        : "Interest - SBA 7(a) Loan",
      isNegative: true,
    }),
    moleculeToRow(get, sellersNoteExpenseTotalMolecule, {
      label: "Seller's Note Total",
      isNegative: true,
      style: singleTopBorderStyle,
    }),
    moleculeToRow(get, sellersNoteExpensePricipalMolecule, {
      label: "Principal - Seller's Note",
      isNegative: true,
    }),
    moleculeToRow(get, sellersNoteExpenseInterestMolecule, {
      label: "Interest - Seller's Note",
      isNegative: true,
    }),
    createGroupEndRow(),
    moleculeToRow(get, totalDebtServiceMolecule, {
      label: "Total Debt Service",
      isNegative: true,
      style: doubleTopBorderStyle,
    }),
    createGroupEndRow(),

    moleculeToRow(get, distributableFreeCashFlowMolecule, {
      label: "Distributable Free Cash Flow",
      style: greyHighlightStyle,
    }),

    createEmptyRow(),

    createGroupStartRow(),
    moleculeToRow(get, distributableFreeCashFlowMolecule, {
      label: "Distributable Free Cash Flow",
      style: boldStyle,
    }),
    createGroupStartRow(),
    moleculeToRow(get, totalEquityInvestorDistributionsMolecule, {
      label: "Equity Investor Distributions",
      isNegative: true,
      style: singleTopBorderStyle,
    }),
    moleculeToRow(get, totalEquityPreferredDividendsMolecule, {
      label: "Preferred Dividends",
      isNegative: true,
    }),
    moleculeToRow(get, totalEquityPrincipalRepaymentsMolecule, {
      label: "Principal Repayments",
      isNegative: true,
    }),
    moleculeToRow(get, totalEquityProfitDistributionsMolecule, {
      label: "Profit Distributions",
      isNegative: true,
    }),
    createGroupEndRow(),
    createGroupEndRow(),

    moleculeToRow(get, useableFreeCashFlowMolecule, {
      label: "Usable Free Cash Flow",
      style: greyHighlightStyle,
    }),
  ];

  const rowGroups: { start: number; end: number }[] = [];
  const cleanedRows: typeof rows = [];
  let currentGroupStartIndex: number | null = null;

  rows.forEach((row) => {
    if (row[0] === "GROUP_START") {
      currentGroupStartIndex = cleanedRows.length;
    } else if (row[0] === "GROUP_END") {
      if (currentGroupStartIndex !== null) {
        rowGroups.push({
          start: currentGroupStartIndex,
          end: cleanedRows.length - 1,
        });
        currentGroupStartIndex = null;
      }
    } else {
      cleanedRows.push(row);
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(cleanedRows);

  const COL_WIDTH = 260;
  const COL_INDEX = 0;
  if (!ws["!cols"]) ws["!cols"] = [];
  if (!ws["!cols"][COL_INDEX]) ws["!cols"][COL_INDEX] = { wch: 8 };
  ws["!cols"][COL_INDEX].wpx = COL_WIDTH;

  rowGroups.forEach(({ start, end }) => groupWorksheetRows(ws, start, end));

  return ws;
};

export const historicalsExportAtom = atom(null, async (get) => {
  const XLSX = await XLSXStub();
  const ws = buildHistoricalsWorksheet(get, XLSX);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Historicals");

  const fmt = "xlsx";
  const filename = `historicals-${new Date()
    .toISOString()
    .replace(/:/g, "-")
    .replace(/\..+/, "")}.${fmt}`;

  XLSX.writeFile(wb, filename);

  return;
});
